import * as React from "react";
import { inject, observer } from "mobx-react";
import { WizardFlightPWAProps, WizardFlightPWAViewProps } from "./typings";
import { withRouter } from "react-router";
import { WizardFlightPWAView } from "./WizardFlightPWAView";
import { FlightConfig } from "../../../stores/wizard/config/staticConfigs/flight";
import { FlightWizardState } from "../../../stores/wizard/state/flight";
import { useLocalization } from "@shared-ui/localization-context";
import { ClassCodes } from "src/components/shared/PreferredClassInput/PreferredClassInput";
import { WizardFlightPWALPView } from "./views/WizardFlightPWALP";
import { PreferredAirline } from "src/components/shared/PreferredAirlineInput/PreferredAirlineInput";
import { FlightType } from "src/stores/wizard/state/typings";
import { RefProviderRoot } from "@shared-ui/ref-context";

//for URL construction
const flightClassCodeMap = {
  p: "cabinclass:premium",
  b: "cabinclass:business",
  f: "cabinclass:first",
  e: "cabinclass:economy",
  default: "cabinclass:economy",
};

export enum Views {
  DEFAULT = "default",
  LANDING_PAGE = "landing-page",
}

const WizardLPWithRefWrapper: React.FunctionComponent<WizardFlightPWAViewProps> = (props) => {
  return (
    <RefProviderRoot>
      <WizardFlightPWALPView {...props} />
    </RefProviderRoot>
  );
};

@inject("wizardState", "routeStore", "context", "compositionStore")
@observer
export class WizardFlightPWA extends React.Component<WizardFlightPWAProps> {
  public onSelectPreferredClassCode = (classCode: ClassCodes) => {
    this.props.wizardState.flightWizardState.setFlightClass(classCode);
  };

  public onSelectPreferredAirline = (preferredAirline: PreferredAirline) => {
    this.props.wizardState.flightWizardState.setFlightAirline(preferredAirline);
  };

  private updateSubLOB = (flightType: FlightType) => {
    this.props.wizardState.flightWizardState.updateSubLOBState(flightType);
  };

  //Gets the URL fragment for the "main" flight; if the multi city option is selected, additional legs are added to the URL via another path
  public static getLegURLFragment(flightWizardState: FlightWizardState, flightConfig: FlightConfig): string[] {
    const legs: string[] = new Array(2);

    const rawFormatForSubmission = flightConfig.date.ISOSubmissionSupport
      ? flightConfig.date.format
      : flightWizardState.localDateFormat;

    const { formatDate } = useLocalization();

    const from = "from:";
    const origin = flightWizardState.location.origin.value;
    const comma = ",";
    const to = "to:";
    const destination = flightWizardState.location.destination.value;
    const departure = "departure:";
    const startDate = formatDate(flightWizardState.date.start, {
      raw: rawFormatForSubmission,
    });
    const departureTime = "TANYT";

    let leg1;
    leg1 = from + origin + comma + to + destination + comma + departure + startDate + departureTime;
    legs[0] = leg1;

    if (flightWizardState.subLOBState === FlightType.ROUND_TRIP) {
      let leg2;
      const endDate = formatDate(flightWizardState.date.end, {
        raw: rawFormatForSubmission,
      });
      leg2 = from + destination + comma + to + origin + comma + departure + endDate + departureTime;
      legs[1] = leg2;
    }

    return legs;
  }

  public static getFlightClassCodeForURL(flightClassCode: ClassCodes): string {
    return flightClassCodeMap[flightClassCode];
  }

  constructor(props: WizardFlightPWAProps) {
    super(props);
    const flightWizardState = this.props.wizardState.flightWizardState;
    flightWizardState.overrideConfig(() => {
      flightWizardState.config.elementId = this.props.flexComponent.id;
      flightWizardState.config.enableAddHotel = this.props.flexComponent.model.enableAddHotel;
      flightWizardState.config.enableAddCar = this.props.flexComponent.model.enableAddCar;
      flightWizardState.config.heading.fixedTitle = this.props.flexComponent.model.fixedLobTitle;
      flightWizardState.config.lastSearch.useLastSearchForSingleLOB = this.props.flexComponent.model.useLastSearchForSingleLOB;
      flightWizardState.config.heading.lobSubtitleBreakPoint = this.props.flexComponent.model.lobSubtitleBreakPoint;
      flightWizardState.config.heading.hideLobTitle = this.props.flexComponent.model.hideLobTitle;
      if (this.props.flexComponent.model.titleFromPageHeader) {
        flightWizardState.config.heading.differentiatedTitles = false;
      }
      if (this.props.flexComponent.model.subTitleFromPageSubHeader) {
        flightWizardState.config.heading.lobSubtitleBreakPoint = "on";
      }
    });
    flightWizardState.updateDateFromConfig();
    this.props.wizardState.flightWizardState.localDateFormat = this.props.context.localDateFormat;
  }

  public componentDidMount() {
    const {
      flexComponent,
      wizardState: { flightWizardState, globalWizardState },
    } = this.props;

    if (flexComponent.model.view === Views.LANDING_PAGE) {
      flightWizardState.updateStateFromContext(this.props.context);
    }

    if (globalWizardState.shouldUpdateCookie) {
      flightWizardState.updateStateFromCookie();
    }
  }

  public render() {
    const views = {
      [Views.DEFAULT]: WizardFlightPWAView,
      [Views.LANDING_PAGE]: WizardLPWithRefWrapper,
    };

    const ViewComponent = views[this.props.flexComponent.model.view ?? Views.DEFAULT] || views[Views.DEFAULT];

    return (
      <ViewComponent
        {...this.props}
        onSelectFlightType={this.updateSubLOB}
        onSelectPreferredClassCode={this.onSelectPreferredClassCode}
        onSelectPreferredAirline={this.onSelectPreferredAirline}
      />
    );
  }
}

export default withRouter(WizardFlightPWA);
